import React, { useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import { useIntl, IntlShape } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getSeriesById as getSeriesByIdThunk } from '../../../../store/series/thunks';
import { resetGetSeriesById } from '../../../../store/series/slice';
import { CalendarEventStatusENUM } from '../../../../types';
import { buildSeriesTimelineRows, SeriesTimelineRow } from '../../util/seriesTimeline';

import styles from './CustomerSearchSection.module.scss';

type SeriesContextSectionProps = {
  seriesId: string;
  isFormDirty: boolean;
};

type DotKind = 'completed' | 'cancelled' | 'needsOutcomePast' | 'needsOutcomeScheduled';

type TimelineRow = {
  id: string;
  label: string;
  dateLabel: string;
  dot: DotKind;
};

// Per-event dot picks the same palette as the HA Overview customer card —
// outcome-driven, with future ACTIVE no-outcome events rendered gray
// (legitimately scheduled, no action needed yet).
function pickDot(row: SeriesTimelineRow): DotKind {
  if (row.variant === 'completed') {
    return 'completed';
  }
  if (row.variant === 'cancelled') {
    return 'cancelled';
  }
  const isFutureActive = !row.isPast && row.event.status === CalendarEventStatusENUM.ACTIVE;
  return isFutureActive ? 'needsOutcomeScheduled' : 'needsOutcomePast';
}

function rowLabel(row: SeriesTimelineRow, intl: IntlShape): string {
  if (row.isFirstConsultation) {
    return intl.formatMessage({ id: 'service.firstConsultation' });
  }
  const n = row.followUpSeq ?? '';
  return intl.formatMessage({ id: 'seriesbooking.appointment.followUp' }, { n }).trim();
}

function SeriesContextSectionComponent({ seriesId, isFormDirty }: SeriesContextSectionProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const seriesState = useAppSelector((state) => state.series.getSeriesById);
  const form = useFormContext();

  useEffect(() => {
    dispatch(getSeriesByIdThunk({ id: seriesId }));
  }, [dispatch, seriesId]);

  useEffect(() => () => {
    dispatch(resetGetSeriesById());
  }, [dispatch]);

  // Pre-select the series employee once per loaded series, so new follow-ups
  // default to the first-consult employee. Source priority:
  //   1. series.employeeId (canonical, kept in sync by booking-service)
  //   2. rootEvent.suggestedEmployee (fallback for series predating that sync)
  //
  // Keyed on series.id so a refetch of the same series doesn't re-apply and
  // overwrite a deliberate "Any available employee" (null) selection.
  const loadedSeriesId = seriesState.data && seriesState.data.series.id;
  useEffect(() => {
    if (!seriesState.data) return;
    if (form.getValues('employee')) return;
    // Suppress pre-select when the user has touched any field in this booking
    // session. This catches the remount-after-tab-switch case where a
    // deliberate "Any available employee" choice would otherwise be reverted
    // because the new useForm instance has empty dirtyFields.
    if (isFormDirty) return;
    const { series, events } = seriesState.data;
    let seriesEmployee: string | null = series.employeeId ?? null;
    if (!seriesEmployee && series.rootEventId) {
      const rootEvent = events.find((e) => e.id === series.rootEventId);
      seriesEmployee = (rootEvent?.eventTypeData as any)?.suggestedEmployee ?? null;
    }
    if (seriesEmployee) {
      form.setValue('employee', seriesEmployee, { shouldDirty: true, shouldValidate: true });
    }
  }, [loadedSeriesId]);

  if (seriesState.loading || seriesState.data === null) {
    return (
      <div className={styles.seriesContext} data-testid="series-context-loading">
        <CircularProgress size={16} />
      </div>
    );
  }

  if (seriesState.error !== null) {
    return (
      <div className={styles.seriesContext} data-testid="series-context-error">
        {intl.formatMessage({ id: 'common.defaultError' })}
      </div>
    );
  }

  const seriesRows = buildSeriesTimelineRows({
    events: seriesState.data.events,
    rootEventId: seriesState.data.series.rootEventId,
    nowMs: Date.now(),
  });

  const rows: TimelineRow[] = seriesRows.map((row) => ({
    id: row.event.id,
    label: rowLabel(row, intl),
    dateLabel: row.event.date,
    dot: pickDot(row),
  }));

  const followUpCount = seriesRows.filter((r) => r.followUpSeq !== null).length;
  const nextSeq = followUpCount + 1;
  const nextLabel = intl.formatMessage({ id: 'seriesbooking.appointment.followUp' }, { n: nextSeq });

  const dotClass: Record<DotKind, string> = {
    completed: styles.timelineDotCompleted,
    cancelled: styles.timelineDotCancelled,
    needsOutcomePast: styles.timelineDotPending,
    needsOutcomeScheduled: styles.timelineDotScheduled,
  };

  return (
    <div className={styles.seriesContext} data-testid="series-context-section">
      <div className={styles.seriesContextLabel}>
        {intl.formatMessage({ id: 'seriesbooking.booking.series' })}
      </div>

      {rows.map((row) => (
        <div key={row.id} className={styles.timelineRow} data-testid={`series-timeline-row-${row.id}`}>
          <div className={dotClass[row.dot]}>
            {row.dot === 'completed' ? (
              <CheckIcon className={styles.timelineDotIcon} />
            ) : (
              <ReplayIcon className={styles.timelineDotIconFlipped} />
            )}
          </div>
          <span className={styles.timelineRowLabel}>{row.label}</span>
          <span className={styles.timelineRowDate}>{row.dateLabel}</span>
        </div>
      ))}

      <div className={styles.timelineSeparator} />

      <div className={styles.timelineRow} data-testid="series-timeline-next">
        <div className={styles.timelineDotNext}>
          <AddIcon className={styles.timelineDotIconNext} />
        </div>
        <span className={styles.timelineNextLabel}>{nextLabel}</span>
      </div>

      <div className={styles.timelineHint} data-testid="series-timeline-hint">
        {intl.formatMessage(
          { id: 'seriesbooking.booking.youAreBooking' },
          { label: nextLabel },
        )}
      </div>
    </div>
  );
}

export const SeriesContextSection = React.memo(SeriesContextSectionComponent);
